/**
 * PatientInvoicesList Component
 *
 * List of patient invoices with empty state and load more functionality.
 */

import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { ReceiptLong as NoReceiptIcon, KeyboardArrowDown } from '@mui/icons-material';

import type { InvoiceResponse } from '@store/generated/payments';

import { PatientInvoiceCard } from './PatientInvoiceCard';

export interface PatientInvoicesListProps {
  invoices: InvoiceResponse[];
  hasMore: boolean;
  isLoadingMore: boolean;
  onView: (invoice: InvoiceResponse) => void;
  onDownload: (invoice: InvoiceResponse) => void;
  onLoadMore: () => void;
}

/**
 * List of patient invoices with empty state and load more
 */
export function PatientInvoicesList({
  invoices,
  hasMore,
  isLoadingMore,
  onView,
  onDownload,
  onLoadMore,
}: PatientInvoicesListProps) {
  if (invoices.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
          px: 2,
          textAlign: 'center',
        }}
      >
        <NoReceiptIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5 }} />
        <Typography variant="body2" color="text.secondary">
          No invoices found for this patient.
        </Typography>
      </Box>
    );
  }

  // Sort invoices by date (newest first)
  const sortedInvoices = [...invoices].sort((a, b) => {
    return new Date(b.created).getTime() - new Date(a.created).getTime();
  });

  return (
    <Stack spacing={1}>
      {sortedInvoices.map(invoice => (
        <PatientInvoiceCard
          key={invoice.id}
          invoice={invoice}
          onView={onView}
          onDownload={onDownload}
        />
      ))}

      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <Button
            variant="text"
            size="small"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            startIcon={
              isLoadingMore ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <KeyboardArrowDown />
              )
            }
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {isLoadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

export default PatientInvoicesList;
